import { useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  AlertCircle,
  Check,
  Inbox,
  RefreshCw,
  Code2,
  Trash2,
  Loader2,
} from "lucide-react";

import {
  ToolsViewConfiguration,
  type ToolsMode,
  type RuleFlags,
} from "./ToolsViewConfiguration";
import { JsonTree } from "./JsonTree";

/**
 * Field Item panel — header-style extracted fields.
 *
 * Renders the eroyal shape that ocr-ui's old FieldItem.tsx consumed:
 *
 *   extracted_content.fielditem = {
 *     "<groupName>": [
 *       { field_name, value, boundary },
 *       ...
 *     ],
 *     ...
 *   }
 *
 * Falls back gracefully to:
 *  - `extracted_content` as a flat record of `{value, boundary}` cells
 *  - Array of identifier objects `[{name, value, ...}]`
 *  - Plain key/value record
 *
 * When nothing recognizable is found, the empty state surfaces the raw
 * response so the user can copy it back for shape mapping.
 */

export type FieldEdits = Record<string, string>;

export function fieldEditKey(group: string, fieldName: string): string {
  return `field${group}${fieldName}`;
}

/**
 * Public-facing FieldRow type — exported so callers like the
 * VerificationCockpit's image-marker builder can reuse `parseFieldItemRows`
 * to derive bounding boxes from the projection response.
 */
export interface ParsedFieldRow {
  group: string;
  field_name: string;
  value: string;
  /** "x,y,w,h" string when present, "" otherwise. */
  boundary: string;
}

interface FieldItemPanelProps {
  /** The page's extracted_content (object). */
  fields?: unknown;
  layoutId?: string;
  /** Active boundary highlight — sync with the doc image overlay. */
  activeBoundary?: string | null;
  onSelectField?: (selection: {
    group: string;
    fieldName: string;
    value: string;
    boundary: string;
  }) => void;
  edits?: FieldEdits;
  onEditField?: (group: string, fieldName: string, value: string) => void;
  isLoading?: boolean;
  onProcess?: () => void;
  /** Forwarded to the Process button so it spins while the parent mutation runs. */
  isProcessing?: boolean;
  onManualAdjustment?: () => void;
  onViewRules?: () => void;
  onRetry?: () => void;
  // Tools-view configuration (mode + rule flags) is owned by the parent so
  // it can be shared with the document overlay.
  mode?: ToolsMode;
  onModeChange?: (mode: ToolsMode) => void;
  ruleFlags?: RuleFlags;
  onRuleFlagsChange?: (next: RuleFlags) => void;
  /** When true, the ToolsViewConfiguration header is hidden — useful when
   *  the panel is rendered inside another container (e.g. as the left-hand
   *  results pane in View Rules mode) where the configuration would
   *  duplicate the editor on the right. */
  hideConfiguration?: boolean;
  /**
   * When true, renders a "Select All Field Items" row above the list
   * plus a per-row checkbox. The panel manages selection internally
   * and fires `onDeleteFields` with the selected rows when the user
   * confirms a bulk delete.
   */
  selectable?: boolean;
  /**
   * Called with the list of selected fields when the reviewer clicks
   * the Delete button on the select-all bar. The parent owns the
   * actual delete (save modified content / clear collection / etc.) so
   * the panel doesn't have to know about the backend wiring.
   */
  onDeleteFields?: (
    fields: { group: string; field_name: string }[],
  ) => void;
  /** Disables the Delete button while a parent mutation is in flight. */
  isDeleting?: boolean;
}

interface FieldRow {
  /** Backend group ("Header", "Footer", "Other", …). */
  group: string;
  /** field_name from backend, used as both label and edit key. */
  field_name: string;
  value: string;
  boundary: string;
}

function asString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

// function readConfidence(v: unknown): number | null {
//   if (typeof v !== "number" || Number.isNaN(v)) return null;
//   return v > 1 ? Math.min(1, v / 100) : v;
// }

function rowFromIdentifierObject(
  obj: Record<string, unknown>,
  group: string,
  fallbackName?: string,
): FieldRow | null {
  const name =
    (typeof obj.field_name === "string" && obj.field_name) ||
    (typeof obj.name === "string" && obj.name) ||
    (typeof obj.key === "string" && obj.key) ||
    (typeof obj.identifier === "string" && obj.identifier) ||
    fallbackName;
  if (!name) return null;
  const value = asString(obj.value ?? obj.field_value ?? obj.text);
  const boundary =
    typeof obj.boundary === "string" ? obj.boundary : "";
  return { group, field_name: String(name), value, boundary };
}

function unwrapData(payload: unknown): unknown {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const obj = payload as Record<string, unknown>;
    if ("data" in obj) return obj.data;
  }
  return payload;
}

/** Locate a fielditem-shaped block anywhere in the response tree. */
function findFieldItemBlock(input: unknown): unknown {
  if (!input || typeof input !== "object") return undefined;
  if (Array.isArray(input)) {
    for (const item of input) {
      const found = findFieldItemBlock(item);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  const obj = input as Record<string, unknown>;
  // Direct hit on common keys.
  for (const key of ["fielditem", "field_item", "fields", "extracted_fields"]) {
    if (obj[key] !== undefined) return obj[key];
  }
  // Recurse into nested values. Skip large arrays of unknowns.
  for (const v of Object.values(obj)) {
    if (v && typeof v === "object") {
      const found = findFieldItemBlock(v);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/**
 * Walk an arbitrary object and emit FieldRows for any leaf with a
 * `field_name`/`value` shape OR any scalar value keyed by a string.
 * Used as a last-resort fallback when the canonical fielditem path
 * doesn't match.
 */
function harvestRows(input: unknown, parentGroup: string, rows: FieldRow[]): void {
  if (!input || typeof input !== "object") return;

  if (Array.isArray(input)) {
    for (const item of input) {
      if (item && typeof item === "object") {
        const r = rowFromIdentifierObject(item as Record<string, unknown>, parentGroup);
        if (r) {
          rows.push(r);
        } else {
          harvestRows(item, parentGroup, rows);
        }
      }
    }
    return;
  }

  const obj = input as Record<string, unknown>;

  // Object with field_name/value at the top → treat as one row.
  if ("field_name" in obj || "name" in obj || "key" in obj) {
    const r = rowFromIdentifierObject(obj, parentGroup);
    if (r) {
      rows.push(r);
      return;
    }
  }

  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "object") {
      // {value, boundary} cell.
      if (!Array.isArray(v)) {
        const cell = v as Record<string, unknown>;
        if ("value" in cell || "boundary" in cell) {
          rows.push({
            group: parentGroup,
            field_name: k,
            value: asString(cell.value),
            boundary: typeof cell.boundary === "string" ? cell.boundary : "",
          });
          continue;
        }
      }
      // Recurse with this key as the group.
      harvestRows(v, k, rows);
    } else {
      const s = asString(v);
      if (s) rows.push({ group: parentGroup, field_name: k, value: s, boundary: "" });
    }
  }
}

/**
 * Parse extracted_content into FieldRows. Multi-stage, stops at the first
 * stage that yields rows:
 *   1. Find a `fielditem`/`field_item`/`fields` block anywhere in the tree.
 *      - If it's `Record<group, items>` → group by key.
 *      - If it's `[items]` → group "Fields".
 *   2. Top-level record of `{value, boundary}` cells under extracted_content.
 *   3. Last-resort recursive harvest of any scalar/identifier-shaped data.
 */
export function parseFieldItemRows(input: unknown): ParsedFieldRow[] {
  return parseFieldRows(input);
}

function parseFieldRows(input: unknown): FieldRow[] {
  const rows: FieldRow[] = [];
  const data = unwrapData(input);
  if (!data || typeof data !== "object") return rows;

  // -------- Stage 1: find the canonical fielditem block, anywhere. ----
  const fiBlock = findFieldItemBlock(data);
  if (fiBlock !== undefined) {
    if (Array.isArray(fiBlock)) {
      for (const item of fiBlock) {
        if (item && typeof item === "object") {
          const r = rowFromIdentifierObject(
            item as Record<string, unknown>,
            "Fields",
          );
          if (r) rows.push(r);
        }
      }
    } else if (fiBlock && typeof fiBlock === "object") {
      for (const [group, val] of Object.entries(fiBlock as Record<string, unknown>)) {
        if (Array.isArray(val)) {
          for (const item of val) {
            if (item && typeof item === "object") {
              const r = rowFromIdentifierObject(
                item as Record<string, unknown>,
                group,
              );
              if (r) rows.push(r);
            }
          }
        } else if (val && typeof val === "object") {
          for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
            if (v && typeof v === "object" && !Array.isArray(v)) {
              const cell = v as Record<string, unknown>;
              if ("value" in cell || "boundary" in cell) {
                rows.push({
                  group,
                  field_name: k,
                  value: asString(cell.value),
                  boundary:
                    typeof cell.boundary === "string" ? cell.boundary : "",
                });
                continue;
              }
              const r = rowFromIdentifierObject(
                { field_name: k, ...(v as Record<string, unknown>) },
                group,
              );
              if (r) rows.push(r);
            } else {
              rows.push({ group, field_name: k, value: asString(v), boundary: "" });
            }
          }
        } else {
          rows.push({ group: "Fields", field_name: group, value: asString(val), boundary: "" });
        }
      }
    }
    if (rows.length > 0) return rows;
  }

  // -------- Stage 2: flat `{value, boundary}` cells. ------------------
  const obj = data as Record<string, unknown>;
  for (const [key, raw] of Object.entries(obj)) {
    if (key === "image_path" || key === "image_size" || key === "page_no") continue;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const cell = raw as Record<string, unknown>;
      if ("value" in cell || "boundary" in cell) {
        rows.push({
          group: "Other",
          field_name: key,
          value: asString(cell.value),
          boundary: typeof cell.boundary === "string" ? cell.boundary : "",
        });
      }
    }
  }
  if (rows.length > 0) return rows;

  // -------- Stage 3: last-resort recursive harvest. -------------------
  harvestRows(data, "Other", rows);
  return rows;
}

function confidenceTone(c: number | null) {
  if (c === null) return null;
  if (c >= 0.9) return { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", Icon: Check };
  if (c >= 0.7) return { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", Icon: AlertCircle };
  return { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200", Icon: AlertCircle };
}

export function FieldItemPanel({
  fields,
  layoutId,
  activeBoundary,
  onSelectField,
  edits,
  onEditField,
  isLoading,
  onProcess,
  isProcessing,
  onManualAdjustment,
  onViewRules,
  onRetry,
  mode,
  onModeChange,
  ruleFlags,
  onRuleFlagsChange,
  hideConfiguration,
  selectable = false,
  onDeleteFields,
  isDeleting = false,
}: FieldItemPanelProps) {
  const emitSelect = (row: FieldRow) => {
    onSelectField?.({
      group: row.group,
      fieldName: row.field_name,
      value: row.value,
      boundary: row.boundary,
    });
  };
  const [search, setSearch] = useState("");
  // Selection lives in the panel — parent only cares about the final
  // list at delete time. Keyed by `${group}::${field_name}` to avoid
  // collisions across groups (e.g. a "Total" field in Header vs Footer).
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const fieldKey = (group: string, fieldName: string) =>
    `${group}::${fieldName}`;
  // Clear selection whenever the underlying field set changes (page
  // change, new projection, etc.) so a stale selection can't trigger
  // a delete on the new data.
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [fields]);

  const rows = useMemo(() => parseFieldRows(fields), [fields]);
  const editsMap = edits ?? {};

  const filteredRows = useMemo<FieldRow[]>(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.field_name.toLowerCase().includes(q) ||
        r.group.toLowerCase().includes(q) ||
        r.value.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const orderedGroups = useMemo<string[]>(() => {
    const seen: string[] = [];
    for (const r of filteredRows) if (!seen.includes(r.group)) seen.push(r.group);
    return seen;
  }, [filteredRows]);

  const grouped = useMemo<Record<string, FieldRow[]>>(() => {
    const map: Record<string, FieldRow[]> = {};
    for (const row of filteredRows) {
      if (!map[row.group]) map[row.group] = [];
      map[row.group].push(row);
    }
    return map;
  }, [filteredRows]);

  const totalRows = filteredRows.length;
  const activeIndex =
    activeBoundary && filteredRows.length
      ? filteredRows.findIndex((r) => r.boundary === activeBoundary)
      : -1;

  const goToRelative = (delta: number) => {
    if (filteredRows.length === 0) return;
    const next =
      activeIndex < 0
        ? 0
        : (activeIndex + delta + filteredRows.length) % filteredRows.length;
    emitSelect(filteredRows[next]);
  };

  return (
    <div className="flex min-h-full flex-col bg-white">
      {hideConfiguration ? null : (
        <ToolsViewConfiguration
          layoutId={layoutId}
          mode={mode}
          onModeChange={onModeChange}
          ruleFlags={ruleFlags}
          onRuleFlagsChange={onRuleFlagsChange}
          onProcess={onProcess}
          processing={isProcessing}
          onManualAdjustment={onManualAdjustment}
          onViewRules={onViewRules}
        />
      )}

      {/* Results header */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-gray-900">Results</h3>
        <div className="flex items-center gap-2">
          {rows.length > 0 ? (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-600">
              {rows.length} {rows.length === 1 ? "field" : "fields"}
            </span>
          ) : null}
          {rows.length > 0 ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goToRelative(-1)}
                disabled={totalRows === 0}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                aria-label="Previous field"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => goToRelative(1)}
                disabled={totalRows === 0}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                aria-label="Next field"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-sm font-medium text-gray-600">
            Loading results...
          </span>
          <SkeletonRows />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onRetry={onRetry} rawData={fields} />
      ) : (
        <>
          {/* Search */}
          <div className="flex-shrink-0 border-b border-gray-100 px-4 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter fields…"
                className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Bulk select / delete bar — only shown when the parent
              opts in via `selectable`. Lives between Search and the
              row list so the master checkbox lines up visually with
              the per-row checkboxes below. */}
          {selectable
            ? (() => {
                const visibleKeys = filteredRows.map((r) =>
                  fieldKey(r.group, r.field_name),
                );
                const selectedCount = selectedKeys.size;
                const allSelected =
                  visibleKeys.length > 0 &&
                  visibleKeys.every((k) => selectedKeys.has(k));
                const toggleAll = () => {
                  if (allSelected) {
                    setSelectedKeys(new Set());
                  } else {
                    setSelectedKeys(new Set(visibleKeys));
                  }
                };
                const triggerDelete = () => {
                  if (selectedCount === 0) return;
                  const toDelete = rows
                    .filter((r) =>
                      selectedKeys.has(fieldKey(r.group, r.field_name)),
                    )
                    .map((r) => ({
                      group: r.group,
                      field_name: r.field_name,
                    }));
                  onDeleteFields?.(toDelete);
                };
                return (
                  <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-100 bg-blue-50/40 px-4 py-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-gray-800">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                        checked={allSelected}
                        ref={(el) => {
                          if (el)
                            el.indeterminate =
                              selectedCount > 0 && !allSelected;
                        }}
                        onChange={toggleAll}
                        aria-label="Select all field items"
                      />
                      Select All Field Items
                    </label>
                    <div className="flex items-center gap-2">
                      {selectedCount > 0 ? (
                        <span className="text-[11px] text-gray-500 tabular-nums">
                          {selectedCount} selected
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={triggerDelete}
                        disabled={selectedCount === 0 || isDeleting}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-1 text-[11px] font-semibold text-rose-700 shadow-sm hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" />
                        {isDeleting ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                );
              })()
            : null}

          {/* Field rows. No inner scroll — the entire right panel
              scrolls together (see VerificationCockpitPage). */}
          <div>
            {totalRows === 0 ? (
              <div className="px-4 py-12 text-center text-xs text-gray-500">
                No fields match your filter.
              </div>
            ) : (
              orderedGroups.map((groupName) => (
                <section key={groupName}>
                  <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white/90 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 backdrop-blur">
                    <span>{groupName}</span>
                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 tabular-nums text-gray-600">
                      {grouped[groupName].length}
                    </span>
                  </div>
                  <ul className="px-2 py-1">
                    {grouped[groupName].map((row) => {
                      const k = fieldEditKey(row.group, row.field_name);
                      const display = k in editsMap ? editsMap[k] : row.value;
                      const isEdited = k in editsMap && editsMap[k] !== row.value;
                      const isActive =
                        !!activeBoundary && activeBoundary === row.boundary;
                      const rowKey = fieldKey(row.group, row.field_name);
                      const isChecked = selectedKeys.has(rowKey);
                      return (
                        <FieldRowItem
                          key={`${row.group}-${row.field_name}`}
                          row={row}
                          display={display}
                          isActive={isActive}
                          isEdited={isEdited}
                          onSelect={() => {
                            emitSelect(row);
                          }}
                          onChange={(next) => onEditField?.(row.group, row.field_name, next)}
                          selectable={selectable}
                          isChecked={isChecked}
                          onToggleChecked={() => {
                            setSelectedKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(rowKey)) next.delete(rowKey);
                              else next.add(rowKey);
                              return next;
                            });
                          }}
                        />
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FieldRowItem({
  row,
  display,
  isActive,
  isEdited,
  onSelect,
  onChange,
  selectable = false,
  isChecked = false,
  onToggleChecked,
}: {
  row: FieldRow;
  display: string;
  isActive: boolean;
  isEdited: boolean;
  onSelect: () => void;
  onChange: (value: string) => void;
  selectable?: boolean;
  isChecked?: boolean;
  onToggleChecked?: () => void;
}) {
  const tone = confidenceTone(null); // confidence not in the eroyal shape
  return (
    <li
      data-active-field={isActive ? "true" : undefined}
      className={`group relative flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
        isChecked
          ? "bg-blue-50/70"
          : isActive
            ? "bg-blue-50"
            : "hover:bg-gray-50"
      }`}
    >
      <span
        className={`absolute inset-y-2 left-0 w-0.5 rounded-full transition-colors ${
          isActive ? "bg-blue-500" : "bg-transparent"
        }`}
        aria-hidden="true"
      />

      {selectable ? (
        <input
          type="checkbox"
          className="ml-1 h-3.5 w-3.5 flex-shrink-0 cursor-pointer accent-blue-600"
          checked={isChecked}
          onChange={() => onToggleChecked?.()}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.field_name}`}
        />
      ) : null}

      <div
        className="w-32 flex-shrink-0 cursor-pointer truncate pl-1.5 text-xs text-gray-500 group-hover:text-gray-700"
        onClick={onSelect}
        title={row.field_name}
      >
        {row.field_name}
      </div>

      <div className="relative flex flex-1 items-center">
        <input
          type="text"
          value={display}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onSelect}
          onClick={onSelect}
          title={display}
          className={`w-full truncate rounded border bg-transparent px-2 py-1 pr-6 text-xs transition focus:outline-none ${
            isActive
              ? "border-blue-400 bg-white ring-2 ring-blue-100"
              : isEdited
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-gray-200 group-hover:border-gray-300"
          } ${display ? "text-gray-900" : "italic text-gray-400"}`}
          placeholder={display ? "" : "N/A"}
        />

        {row.boundary ? (
          <span
            className={`pointer-events-none absolute right-2 h-1.5 w-1.5 rounded-full ${
              isActive ? "bg-blue-500" : "bg-gray-300"
            }`}
            title="Has bounding-box data — click to highlight on document"
          />
        ) : null}

        {tone ? (
          <span
            className={`ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone.bg} ${tone.text} ${tone.ring}`}
          >
            <tone.Icon className="h-2.5 w-2.5" />
          </span>
        ) : null}
      </div>

      {isEdited ? (
        <span
          className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
          title="Edited"
        >
          <RotateCcw className="inline-block h-2.5 w-2.5" />
        </span>
      ) : null}
    </li>
  );
}

function EmptyState({
  onRetry,
  rawData,
}: {
  onRetry?: () => void;
  rawData?: unknown;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const hasRawPayload =
    rawData !== undefined &&
    rawData !== null &&
    !(typeof rawData === "object" && Object.keys(rawData as object).length === 0);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <Inbox className="h-8 w-8 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">No fields extracted yet</p>
          <p className="mt-1 max-w-xs text-xs text-gray-500">
            {hasRawPayload
              ? "The backend returned data but the parser didn't recognize the shape. Toggle the raw view below to inspect."
              : "Run Process above to start extraction, or wait for the backend to populate this section."}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          ) : null}
          {hasRawPayload ? (
            <button
              type="button"
              onClick={() => setShowRaw((s) => !s)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Code2 className="h-3.5 w-3.5" />
              {showRaw ? "Hide raw response" : "View raw response"}
            </button>
          ) : null}
        </div>
      </div>

      {showRaw && hasRawPayload ? (
        <div className="mx-4 mb-6 overflow-auto rounded-lg bg-gray-50 p-3 text-xs">
          <JsonTree value={rawData} initiallyExpanded depth={0} />
        </div>
      ) : null}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="w-full space-y-2 px-4 py-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-3 w-24 rounded bg-gray-100" />
          <div className="h-7 flex-1 rounded-md border border-gray-100 bg-gray-50" />
        </div>
      ))}
    </div>
  );
}
